import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showSuccessFeedbackBar, showErrorFeedbackBar } from 'reducers/ui/uiSlice';
import { UPDATE_ERROR, UPDATE_SUCCESS } from 'shared/constants/feedbackMessages';
import { getFormatGroupTags } from 'services/CommonService';
import { getVisitorTags, updateVisitorTags } from 'services/ClientService';
import DefaultActionButtons from 'components/DefaultActionButtons';
import SectionTitle from 'components/typography/SectionTitle';
import Button from 'components/Button';

export default function HolidayPreferencesTab() {
  const [allGroupTags, setAllGroupTags] = useState({});
  const [yesNoGroupTags, setYesNoGroupTags] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [isTablet, setIsTablet] = useState(window.innerWidth < 768);
  const { userData } = useSelector((state) => state.currentUser);
  const dispatch = useDispatch();

  const onWindowResize = () => {
    setIsTablet(window.innerWidth < 768);
  };

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  const proccessFormatTagsData = (groupFormatTags) => {
    const transformedData = {};

    Object.keys(groupFormatTags).forEach((group) => {
      transformedData[group] = groupFormatTags[group].map((item) => {
        const { tag, format } = item;
        if (tag && tag.key && tag.values) {
          return {
            key: tag.key,
            values: tag.values,
            format,
          };
        }
        console.error(`Invalid tag structure in group "${group}":`, item);
        return null;
      }).filter(Boolean);
    });

    setAllGroupTags(transformedData);
  };

  const proccessVisitorData = (visitorData) => {
    const initialSelectedValues = {};

    Object.keys(visitorData).forEach((groupTag) => {
      const groupData = visitorData[groupTag];
      if (Array.isArray(groupData)) {
        groupData.forEach(({ key, values }) => {
          if (!initialSelectedValues[groupTag]) {
            initialSelectedValues[groupTag] = {};
          }
          initialSelectedValues[groupTag][key] = { key, values };
        });
      } else {
        console.error(`Expected an array for groupTag "${groupTag}", but got:`, groupData);
      }
    });

    setInitialValues(initialSelectedValues);
    setSelectedValues(initialSelectedValues);
  };

  const loadData = useCallback(async () => {
    const GROUP_TAGS = ["Holiday Types", "Holiday Preferences", "Lifestyle"];
    try {
      const { data: groupFormatTagsData } = await getFormatGroupTags(GROUP_TAGS);
      proccessFormatTagsData(groupFormatTagsData);

      const { data: visitorTagsData } = await getVisitorTags(userData.longId);
      proccessVisitorData(visitorTagsData);
    } catch (error) {
      console.error(error);
    }
  }, [userData.longId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const proccessYesNoGroupTags = (groupTags) => {
    const yesNoTags = {};

    Object.keys(groupTags).forEach((groupTag) => {
      groupTags[groupTag].forEach(({ key, values }) => {
        if (values.includes("Yes") && values.includes("No")) {
          yesNoTags[groupTag] = yesNoTags[groupTag] || {};
          yesNoTags[groupTag][key] = { key, values };
        }
      });
    });

    setYesNoGroupTags(yesNoTags);
  };

  useEffect(() => {
    proccessYesNoGroupTags(allGroupTags);
  }, [allGroupTags]);

  const handleYesNoChange = (groupTag, key, value) => {
    setSelectedValues((prev) => {
      if (value === "No") {
        const { [key]: _, ...restKeys } = prev[groupTag] || {};
        if (Object.keys(restKeys).length === 0) {
          const { [groupTag]: __, ...restGroups } = prev;
          return restGroups;
        }
        return {
          ...prev,
          [groupTag]: restKeys,
        };
      }

      return {
        ...prev,
        [groupTag]: {
          ...prev[groupTag],
          [key]: {
            key,
            values: [value],
          },
        },
      };
    });
  };

  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
  };

  const handleValueChange = ({ target: { value } }, groupTag, key) => {
    setSelectedValues((prev) => {
      const isDate = !Number.isNaN(Date.parse(value));
      const updatedGroup = prev[groupTag]?.[key]?.values || [];
  
      const updatedValues = isDate
        ? [value]
        : [...updatedGroup, value];
  
      return {
        ...prev,
        [groupTag]: {
          ...prev[groupTag],
          [key]: {
            key,
            values: updatedValues,
          },
        },
      };
    });
  };

  const removeSelectedValue = (groupTag, key, value) => {
    setSelectedValues((prev) => {
      const updatedValues = { ...prev };

      if (updatedValues[groupTag] && updatedValues[groupTag][key]) {
        const filteredValues = updatedValues[groupTag][key].values.filter((v) => v !== value);

        if (filteredValues.length === 0) {
          delete updatedValues[groupTag][key];

          if (Object.keys(updatedValues[groupTag]).length === 0) {
            delete updatedValues[groupTag];
          }
        } else {
          updatedValues[groupTag][key] = {
            ...updatedValues[groupTag][key],
            values: filteredValues,
          };
        }
      }

      return updatedValues;
    });
  };

  const proccessSelectedData = () => {
    const output = {};

    Object.keys(selectedValues).forEach(category => {
      output[category] = Object.keys(selectedValues[category]).map(subKey => ({
        key: selectedValues[category][subKey].key,
        values: selectedValues[category][subKey].values
      }));
    });
  
    return output;
  };

  const onCancel = () => {
    setSelectedValues(initialValues);
  };

  const onSave = async () => {
    try {
      const proccessedData = proccessSelectedData();
      const { data } = await updateVisitorTags(userData.longId, proccessedData);
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      proccessVisitorData(parsedData);
      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
    } catch (error) {
      console.error(error);
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
    }
  };

  return (
    <div className="flex flex-col min-h-screen p-6">
      <div className={`${isTablet ? 'w-full' : 'w-[50%]'}`}>
      <SectionTitle label="Holiday Preferences" className="text-2xl font-bold text-gray-800 mb-6" />
      <h2>We encourage you to provide some further information that will enable us to better understand your needs, so that we can provide tailored service to you and more relevant marketing information.</h2>

      {Object.keys(allGroupTags).map((groupTag) => {
        const keys = allGroupTags[groupTag]?.map(({ key }) => key)
          .filter((key) => !yesNoGroupTags[groupTag]?.[key]) || [];
        if (keys.length === 0) return null;

        return (
          <div key={groupTag} className="mt-6 bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">{groupTag}</h3>
            {keys.length > 1 ? (
              <>
                <select
                  onChange={handleGroupChange}
                  value={selectedGroup || ''}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>Select {groupTag}</option>
                  {keys.map((key) => (
                    <option key={key} value={key}>{key}</option>
                  ))}
                </select>
                {selectedGroup && allGroupTags[groupTag]?.some(({ key }) => key === selectedGroup) && (
                  <div>
                    {(() => {
                      const selectedGroupData = allGroupTags[groupTag]?.find(({ key }) => key === selectedGroup);

                      if (selectedGroupData?.format === "DATE") {
                        return (
                          <input
                            type="date"
                            onChange={(e) => handleValueChange(e, groupTag, selectedGroup)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        );
                      }

                      return (
                        <select
                          onChange={(e) => handleValueChange(e, groupTag, selectedGroup)}
                          value=""
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="" disabled>Select a value</option>
                          {selectedGroupData?.values
                            ?.filter((value) => !selectedValues[groupTag]?.[selectedGroup]?.values?.includes(value))
                            .map((value) => (
                              <option key={value} value={value}>{value}</option>
                            ))}
                        </select>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <select
                onChange={(e) => handleValueChange(e, groupTag, keys[0])}
                value=""
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Select a value</option>
                {allGroupTags[groupTag]?.[0]?.values
                  ?.filter((value) => !selectedValues[groupTag]?.[keys[0]]?.values?.includes(value))
                  .map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
              </select>
            )}
          </div>
        );
      })}

      {Object.keys(yesNoGroupTags).map((groupTag) => (
        <div key={groupTag} className="mt-6 bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">{groupTag}</h3>
          {Object.keys(yesNoGroupTags[groupTag]).map((key) => (
            <div key={key} className="flex items-center justify-between mb-2">
              <span className="text-gray-600">{key}:</span>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedValues[groupTag]?.[key]?.values?.[0] === "Yes"}
                  onChange={(e) =>
                    handleYesNoChange(groupTag, key, e.target.checked ? "Yes" : "No")
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </label>
            </div>
          ))}
        </div>
      ))}
      </div>

      <div
        className={`${
          isTablet
            ? 'mt-6 bg-white p-4 rounded-lg shadow-md'
            : 'sticky bottom-[100px] right-0 max-w-xs bg-white border-b shadow-md p-4 z-10 ml-auto rounded-lg'
        }`}
      >
        <h2 className="font-bold text-lg text-gray-800">Your Preferences</h2>
        {Object.keys(selectedValues).map((groupTag) => (
          <div key={groupTag} className="mt-4">
            <strong className="block text-gray-700">{groupTag}</strong>
            {Object.keys(selectedValues[groupTag]).map((key) => {
              const { values } = selectedValues[groupTag][key] || { values: [] };
              return values.map((value) => {
                const isDate = !Number.isNaN(Date.parse(value));
                const formattedValue = isDate
                  ? new Date(value).toLocaleDateString('en-GB')
                  : value;

                return (
                  <div
                    key={`${groupTag}-${key}-${value}`}
                    className="flex items-center justify-between mt-2"
                  >
                    <div className="flex flex-col mr-4">
                      <span className="text-sm text-gray-600">{key}:</span>
                      <span className="text-sm text-gray-800">{formattedValue}</span>
                    </div>
                    <Button
                      className="!h-[30px] !inline-block !px-4 !py-0 ml-2 text-sm text-red-500 hover:text-red-700"
                      onClick={() => removeSelectedValue(groupTag, key, value)}
                    >
                      Remove
                    </Button>
                  </div>
                );
              });
            })}
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 left-0 w-full bg-white border-t shadow-md p-4 z-10 flex justify-end">
        <DefaultActionButtons
          onSecondaryButtonClick={onCancel}
          onPrimaryButtonClick={onSave}
          isPrimaryButtonDisabled={JSON.stringify(selectedValues) === JSON.stringify(initialValues)}
          hideSecondaryButton={JSON.stringify(selectedValues) === JSON.stringify(initialValues)}
          containerClassName="flex justify-end"
          secondaryButtonClassName="px-4"
          primaryButtonClassName="px-4"
        />
      </div>
    </div>
  );
}
